import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Zap, ShieldCheck, Sparkles, Award, Server } from 'lucide-react';
import { useT } from '@/lib/i18n';

/**
 * Trust — factual capability cards. No fake certifications, no fake numbers.
 * Every claim here is verifiable in the codebase / product.
 */

const CARDS_FR = [
  { icon: Lock,        title: 'Uploads chiffrés',           body: 'Tes vidéos passent par des connexions HTTPS. Aucune donnée n\'est jamais partagée.' },
  { icon: ShieldCheck, title: 'Ton contenu t\'appartient',  body: 'Tu gardes 100% des droits sur tes clips. Rien n\'est utilisé pour entraîner un modèle tiers.' },
  { icon: Zap,         title: 'Traitement rapide',          body: 'Pipeline moyen de 4 minutes par vidéo — parallelisme FFmpeg + GPU pour Whisper.' },
  { icon: Award,       title: 'Sans watermark (Premium)',   body: 'Plans Creator et Studio : aucun watermark HighlightAI sur tes clips exportés.' },
  { icon: Sparkles,    title: 'IA officielle',              body: 'Powered by OpenAI Whisper + GPT-5.2. Pas de modèle bricolé, pas d\'approximation.' },
  { icon: Server,      title: 'Infra scalable',             body: 'Kubernetes multi-replica, GridFS chunked uploads, retry automatique.' },
];
const CARDS_EN = [
  { icon: Lock,        title: 'Encrypted uploads',          body: 'Your videos travel over HTTPS only. Nothing is ever sold or shared.' },
  { icon: ShieldCheck, title: 'You own your content',       body: 'You keep 100% of the rights. Nothing is used to train third-party models.' },
  { icon: Zap,         title: 'Fast processing',            body: 'Average 4-minute pipeline per video — parallel FFmpeg + GPU-backed Whisper.' },
  { icon: Award,       title: 'No watermark (Premium)',     body: 'Creator and Studio plans: zero HighlightAI watermark on exports.' },
  { icon: Sparkles,    title: 'Official AI',                body: 'Powered by OpenAI Whisper + GPT-5.2. No home-brew models, no approximations.' },
  { icon: Server,      title: 'Scalable infra',             body: 'Kubernetes multi-replica, GridFS chunked uploads, automatic retry.' },
];

const Trust = () => {
  const { lang } = useT();
  const cards = lang === 'fr' ? CARDS_FR : CARDS_EN;
  return (
    <section id="trust" className="py-24 px-6 lg:px-8" data-testid="landing-trust">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] text-xs uppercase tracking-[0.2em] text-zinc-400 mb-5 font-mono">
            {lang === 'fr' ? 'Confiance' : 'Trust'}
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">
            {lang === 'fr' ? 'Pourquoi tu peux nous confier ton contenu' : 'Why you can trust us with your content'}
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.35, delay: i * 0.04 }}
              whileHover={{ y: -2 }}
              className="rounded-2xl border border-white/6 bg-white/[0.02] hover:border-white/15 p-6"
              data-testid={`trust-card-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#D4FF00]/10 border border-[#D4FF00]/25 flex items-center justify-center mb-4">
                <c.icon className="w-5 h-5 text-[#D4FF00]" />
              </div>
              <h3 className="font-heading text-lg font-bold mb-1.5">{c.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{c.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Trust;
